import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useField } from 'react-final-form';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import { IOption } from 'interfaces';

export const MultipleAutocompleteAdapter = ({
  input: { name },
  options,
  optionLabel,
  optionValue,
  initValue,
  setValue,
  freeSolo,
  disabled: disabledProp,
  ...rest
}: any) => {
  const { input, meta } = useField(name);

  const optLabel = optionLabel ?? 'code';
  const optValue = optionValue ?? 'id';

  const [_value, _setValue] = useState<unknown[] | null>(null);
  const lastAppliedInitKey = useRef('');

  const pushFormValues = useCallback(
    (values: readonly unknown[] | null | undefined) => {
      const newValues: string[] = [];
      if (values?.length) {
        values.forEach((value) => {
          if (typeof value === 'string') {
            newValues.push(value ?? '');
          } else if (value && typeof value === 'object') {
            newValues.push((value as Record<string, string>)[optValue] ?? '');
          }
        });
      }
      input.onChange(newValues);
    },
    [input.onChange, optValue]
  );

  const resolveInitSelection = useCallback(
    (safeOptions: IOption[], rawInit: unknown[]): IOption[] => {
      return rawInit
        .filter((x) => x != null)
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            const match = safeOptions.find(
              (o) =>
                o[optValue] === (item as IOption)[optValue] ||
                String(o[optValue]) === String((item as IOption)[optValue])
            );
            return match ?? (item as IOption);
          }
          const match = safeOptions.find(
            (o) => o[optValue] === item || String(o[optValue]) === String(item)
          );
          return match ?? (item as IOption);
        });
    },
    [optValue]
  );

  const onChange = (_e: SyntheticEvent, values: unknown[] | null) => {
    if (!values?.length) {
      _setValue([]);
      pushFormValues([]);
      return;
    }
    _setValue([...values]);
    const newValues: string[] = [];
    values.forEach((value) => {
      if (typeof value === 'string') {
        newValues.push(value ?? '');
      } else if (value && typeof value === 'object') {
        newValues.push((value as Record<string, string>)[optValue] ?? '');
      }
    });
    input.onChange(newValues);
    if (setValue) {
      setValue(values);
    }
  };

  /** When `initValue` is omitted, skip syncing (other forms rely on user input only). */
  const initMultiKey = useMemo(() => {
    if (initValue === undefined) {
      return null;
    }
    if (!Array.isArray(initValue)) {
      return '';
    }
    return initValue
      .filter((x) => x != null)
      .map((o) =>
        typeof o === 'object' && o !== null && optValue in o
          ? String((o as Record<string, unknown>)[optValue] ?? '')
          : String(o)
      )
      .sort()
      .join('|');
  }, [initValue, optValue]);

  const getOptionLabel = (option: IOption | string) => {
    switch (typeof option) {
      case 'string':
        return option;
      default:
        return option[optLabel];
    }
  };

  const isOptionsEmpty = () => {
    if (freeSolo === true) return false;
    return !options.length;
  };

  useEffect(() => {
    if (initMultiKey === null) {
      return;
    }
    const safeOptions = options ?? [];
    if (!safeOptions.length) {
      return;
    }
    if (lastAppliedInitKey.current === initMultiKey) {
      return;
    }
    const rawInit = Array.isArray(initValue) ? initValue.filter((x) => x != null) : [];
    const selected = resolveInitSelection(safeOptions, rawInit);
    _setValue(selected);
    pushFormValues(selected);
    lastAppliedInitKey.current = initMultiKey;
  }, [options, initMultiKey, initValue, pushFormValues, resolveInitSelection]);

  return (
    <Autocomplete
      disableListWrap
      multiple={true}
      freeSolo={freeSolo === true}
      getOptionLabel={(option) =>
        option == null ? '' : typeof option === 'string' ? option : option[optLabel] ?? ''
      }
      options={options}
      disabled={disabledProp ? true : isOptionsEmpty()}
      onChange={onChange}
      value={_value ?? []}
      isOptionEqualToValue={(option, value) => {
        if (typeof value === 'string') {
          return option[optValue] === value || option[optLabel] === value;
        }
        return option[optValue] === value[optValue];
      }}
      renderTags={(value: readonly unknown[], getTagProps) =>
        value.map((option: unknown, index: number) => (
          <Chip
            key={`${getOptionLabel(option as IOption | string)}-${index}`}
            variant="outlined"
            label={getOptionLabel(option as IOption | string)}
            {...getTagProps({ index })}
          />
        ))
      }
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          {typeof option === 'string' ? option : option[optLabel]}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          {...rest}
          helperText={meta.touched ? meta.error : ''}
          error={meta.error && meta.touched}
          fullWidth
        />
      )}
    />
  );
};
